import type { HomeNodeCandidateStorePort } from "@econ-v1/ports";

/**
 * The `node-discovered` DOM event's payload (lan-direct-pairing plan, C3) — the onboarding
 * discovery screen's manual-address "Continue" button
 * (`client/ui/src/shell/onboarding/screens/connect-discovery.ts`) dispatches a
 * `bubbles: true, composed: true` CustomEvent carrying this shape, mirroring
 * `NodeDiscoveryCandidate`'s wire fields (`@econ-v1/ports`'s `network/node-discovery.js`)
 * without importing that type just to satisfy a cast — this composition root only ever
 * reads `address`/`id`.
 */
export interface NodeDiscoveredDetail {
  readonly candidate: { readonly address: string; readonly id: string };
}

export function isNodeDiscoveredDetail(value: unknown): value is NodeDiscoveredDetail {
  if (typeof value !== "object" || value === null) return false;
  const candidate = (value as Record<string, unknown>).candidate;
  if (typeof candidate !== "object" || candidate === null) return false;
  const record = candidate as Record<string, unknown>;
  return typeof record.address === "string" && record.address.length > 0 &&
    typeof record.id === "string" && record.id.length > 0;
}

/**
 * The `node-discovered` handler (lan-direct-pairing plan, C3), factored out of
 * `system/pwa/src/main.ts` so it's unit-testable without a DOM — `store`/`reload` are
 * injected the same way `boot-gate.ts`'s `shouldBootShell` takes its browser-sourced inputs
 * as plain parameters rather than reading `localStorage`/`location` itself. `main.ts` is the
 * only real caller; it passes `homeNodeCandidateStore` and `() => globalThis.location.reload()`.
 *
 * A malformed/garbage `detail` (should never happen — the event is dispatched by
 * `client/ui`'s own typed code — but `event.detail` is `unknown` at this boundary) is a
 * silent no-op: never persists, never reloads.
 *
 * Persisting and reloading (rather than trying to hot-swap the live transport supervisor) is
 * deliberate — see `main.ts`'s call site for why a full reload is the simplest reliable way
 * to make the discovered candidate become the boot-time home node
 * (`kernel-runtime.ts`'s `resolveInitialHomeNode`), and why it loses no data at this point in
 * the flow (onboarding, before any home node — let alone any session data — exists).
 */
export function applyDiscoveredNodeCandidate(
  detail: unknown,
  store: Pick<HomeNodeCandidateStorePort, "write">,
  reload: () => void,
): void {
  if (!isNodeDiscoveredDetail(detail)) return;
  store.write({ address: detail.candidate.address, id: detail.candidate.id });
  reload();
}
